import classNames from 'classnames';
import FJSlider from '../../../../common/components/FJSlider/FJSlider';
import FJToolTip from '../../../../common/components/FJToolTip/FJToolTip';
import styles from './FJEditFloatTool.module.less';
import FJLocalStore from '../../../../store/FJLocalStore';
import PropTypes from 'prop-types';
import FJZoomIcon from '../../../../assets/svg/timeline-zoom-.svg?react';
import FJZoomAddIcon from '../../../../assets/svg/timeline-zoom+.svg?react';
import FJUndoIcon from '../../../../assets/svg/undo.svg?react';
import FJRedoIcon from '../../../../assets/svg/redo.svg?react';
import FJResetIcon from '../../../../assets/svg/reset.svg?react';
import FJComparisonIcon from '../../../../assets/svg/comparison.svg?react';
import FJMoveIcon from '../../../../assets/svg/move.svg?react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useRef } from 'react';
import { useMemo } from 'react';
import { ZOOM_MIN, ZOOM_MAX } from '../../../../config/dataConfig/FJImageManipulationData';
import FJEvent from '../../../../store/FJEvent';
import { FJ_EVENT_TYPE } from '../../../../constants/FJEvenTypeConstant';
const FJEditFloatTool = props => {
    const {
        zoom,
        setZoom,
        hasUndo,
        hasRedo,
        undoHandler,
        redoHandler,
        resetHandler,
        disableReset,
        isMouseMove,
        isHideEditButton,
        changeIsMouseMove,
        disableCompare,
        compareHandler,
        fitZoomAndPositionHandler,
        resizeCanvas,
        setMouseInToolPanel,
        isShowEditCanvas,
        disableFitButton,
        updateBrushSize,
        isSlideCompare,
    } = props;

    const keyUpUpdateIsMouseMove = useRef(true);

    useEffect(() => {
        if (!isMouseMove) {
            keyUpUpdateIsMouseMove.current = true;
        }
    }, [isMouseMove]);

    const handleKeyDown = useCallback(
        event => {
            if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'z') {
                hasRedo && redoHandler();
                return;
            }
            if (event.ctrlKey || event.metaKey) {
                switch (event.key) {
                    case 'z':
                        hasUndo && undoHandler();
                        break;
                    case 'y':
                        hasRedo && redoHandler();
                        break;
                    case '[':
                        updateBrushSize(false);
                        break;
                    case ']':
                        updateBrushSize(true);
                        break;
                    default:
                        break;
                }
            } else {
                if (event.key === ' ' || event.key === 'Spacebar') {
                    changeIsMouseMove(true);
                }
            }
        },
        [undoHandler, redoHandler, hasUndo, hasRedo, changeIsMouseMove, updateBrushSize],
    );

    const handleKeyUp = useCallback(
        event => {
            if (event.key === ' ' || event.key === 'Spacebar') {
                keyUpUpdateIsMouseMove.current && changeIsMouseMove(false);
            }
        },
        [changeIsMouseMove],
    );

    const handleResizeCanvas = useCallback(() => {
        if (!isShowEditCanvas) return;
        resizeCanvas();
    }, [resizeCanvas, isShowEditCanvas]);

    useEffect(() => {
        if (!isShowEditCanvas) return;

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('resize', handleResizeCanvas);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('resize', handleResizeCanvas);
        };
    }, [handleKeyDown, handleKeyUp, handleResizeCanvas, isShowEditCanvas]);

    const onChangeZoom = useCallback(
        value => {
            if (!isShowEditCanvas) return;
            setZoom(value);
        },
        [setZoom, isShowEditCanvas],
    );

    const mouseUpCompare = useCallback(() => {
        if (disableCompare) return;

        compareHandler(false);
        window.removeEventListener('mouseup', mouseUpCompare);
    }, [disableCompare, compareHandler]);

    const mouseDownCompare = useCallback(() => {
        if (disableCompare) return;
        compareHandler(true);

        window.addEventListener('mouseup', mouseUpCompare);
    }, [disableCompare, compareHandler, mouseUpCompare]);

    const mouseDownPanel = useCallback(e => {
        e.stopPropagation();
        FJEvent.dispatch(FJ_EVENT_TYPE.HIDE_QUICK_SELECTION_BOX);
        FJEvent.dispatch(FJ_EVENT_TYPE.HIDE_SIDE_MODAL);
    }, []);

    const zoomOutDisabled = useMemo(() => zoom <= ZOOM_MIN || !isShowEditCanvas, [zoom, isShowEditCanvas]);
    const zoomInDisabled = useMemo(() => zoom >= ZOOM_MAX || !isShowEditCanvas, [zoom, isShowEditCanvas]);

    return (
        <div
            className={classNames(styles.edit_box, { [styles.edit_box_hide_button]: isHideEditButton })}
            onMouseDown={mouseDownPanel}
            onMouseEnter={() => setMouseInToolPanel(true)}
            onMouseLeave={() => setMouseInToolPanel(false)}
        >
            <div className={classNames(styles.undo_redo_box, { [styles.hide]: isHideEditButton })}>
                <FJToolTip title={FJLocalStore._('undo')}>
                    <div
                        onClick={undoHandler}
                        className={classNames(styles.undo_icon_box, { [styles.disabled]: !hasUndo })}
                    >
                        <FJUndoIcon className={styles.undo_icon} />
                    </div>
                </FJToolTip>
                <FJToolTip title={FJLocalStore._('redo')}>
                    <div
                        onClick={redoHandler}
                        className={classNames(styles.redo_icon_box, { [styles.disabled]: !hasRedo })}
                    >
                        <FJRedoIcon className={styles.redo_icon} />
                    </div>
                </FJToolTip>
            </div>
            <div
                className={classNames(styles.reset_icon_box, {
                    [styles.disabled]: disableReset,
                    [styles.hide]: isHideEditButton,
                })}
                onClick={resetHandler}
            >
                <FJResetIcon className={styles.reset_icon} />
                {FJLocalStore._('reset')}
            </div>
            <div
                className={classNames(styles.divider, { [styles.hide]: isHideEditButton })}
                style={{ marginLeft: 8 }}
            />
            <div
                className={classNames(styles.comparison_box, {
                    [styles.disabled]: disableCompare,
                    [styles.active]: isSlideCompare,
                    [styles.comparison_box_hide]: isHideEditButton,
                })}
                onMouseDown={mouseDownCompare}
            >
                <FJComparisonIcon className={styles.comparison_icon} />
                {FJLocalStore._('compare')}
            </div>
            <div className={classNames(styles.divider)} style={{ marginLeft: 8 }} />
            <div className={classNames(styles.fit_box)}>
                <FJToolTip title='image-extender-zoom-out'>
                    <div
                        className={classNames(styles.fit_icon_box, {
                            [styles.disabled]: zoomOutDisabled,
                        })}
                        onClick={() => !zoomOutDisabled && setZoom(Math.max(zoom - 0.1, ZOOM_MIN))}
                    >
                        <FJZoomIcon className={styles.fit_icon} />
                    </div>
                </FJToolTip>
                <FJSlider
                    className={classNames(styles.fit_slider, { [styles.disabled]: !isShowEditCanvas })}
                    max={ZOOM_MAX}
                    min={ZOOM_MIN}
                    value={zoom}
                    disabled={!isShowEditCanvas}
                    onChange={onChangeZoom}
                />
                <FJToolTip title='image-extender-zoom-in'>
                    <div
                        className={classNames(styles.fit_icon_box, {
                            [styles.disabled]: zoomInDisabled,
                        })}
                        onClick={() => !zoomInDisabled && setZoom(Math.min(zoom + 0.1, ZOOM_MAX))}
                    >
                        <FJZoomAddIcon className={styles.fit_icon} />
                    </div>
                </FJToolTip>
                <div
                    className={classNames(styles.fit_btn, {
                        [styles.disabled]: disableFitButton || !isShowEditCanvas,
                    })}
                    onClick={disableFitButton || !isShowEditCanvas ? () => {} : fitZoomAndPositionHandler}
                >
                    {FJLocalStore._('fit')}
                </div>
            </div>
            <div className={classNames(styles.divider)} style={{ marginLeft: 12 }} />
            <FJToolTip title={FJLocalStore._('move')}>
                <div
                    className={classNames(styles.move_icon_box, {
                        [styles.active]: isMouseMove,
                        [styles.disabled]: !isShowEditCanvas,
                    })}
                    onClick={() => {
                        if (!isShowEditCanvas) return;
                        keyUpUpdateIsMouseMove.current = isMouseMove;
                        changeIsMouseMove(!isMouseMove);
                    }}
                >
                    <FJMoveIcon className={styles.move_icon} />
                </div>
            </FJToolTip>
        </div>
    );
};

FJEditFloatTool.propTypes = {
    zoom: PropTypes.number.isRequired,
    setZoom: PropTypes.func.isRequired,
    hasUndo: PropTypes.bool.isRequired,
    hasRedo: PropTypes.bool.isRequired,
    undoHandler: PropTypes.func.isRequired,
    redoHandler: PropTypes.func.isRequired,
    resetHandler: PropTypes.func.isRequired,
    disableReset: PropTypes.bool.isRequired,
    isMouseMove: PropTypes.bool.isRequired,
    changeIsMouseMove: PropTypes.func.isRequired,
    disableCompare: PropTypes.bool.isRequired,
    compareHandler: PropTypes.func.isRequired,
    fitZoomAndPositionHandler: PropTypes.func.isRequired,
    resizeCanvas: PropTypes.func.isRequired,
    setMouseInToolPanel: PropTypes.func.isRequired,
    isHideEditButton: PropTypes.bool.isRequired,
    isShowEditCanvas: PropTypes.bool.isRequired,
    disableFitButton: PropTypes.bool.isRequired,
    isSlideCompare: PropTypes.bool.isRequired,
    updateBrushSize: PropTypes.func.isRequired,
};

export default FJEditFloatTool;
